import { ReactNode } from "react";

import { IgLink, IgTypography } from "@intelligems/ig-theme";
import { ExperienceCategory } from "@intelligems/ig-types";
import {
  CORE_CONTENT_TEST_KEYS,
  PLUS_TEST_KEYS,
} from "@src/components/LimitAccess/subscription-tier-tests";
import { contactSupport } from "@src/utils/contactSupport";
import { ExperienceCreationCard } from "@src/components/Dialogs/CreateExperienceDialog/types";

export type RunModeGatingContext = {
  canCreateShippingTest: boolean;
};

// Gating that only applies to one run mode. Shown on the run-mode page's
// "Run a Test" / "Create a Personalization" buttons.
export const getRunModeDisabledReason = (
  card: ExperienceCreationCard,
  category: ExperienceCategory,
  { canCreateShippingTest }: RunModeGatingContext
): string | undefined => {
  if (category === "experiment") {
    if (!card.test) {
      return "This experience can only be created as a personalization.";
    }
    if (card.test.key === "shipping_experiment" && !canCreateShippingTest) {
      return "Only one shipping test can be run at a time. End your current shipping test to start a new one.";
    }
  }

  if (category === "personalization" && !card.personalization) {
    return "This experience can only be run as a test.";
  }

  return undefined;
};

export type FirstCardGatingContext = {
  isPlusOrPartnerPlan: boolean;
  hasPostPurchaseAccess: boolean;
  hasWriteOrders: boolean;
  hasStorefrontToken: boolean;
  hasCartMetafields: boolean;
  canCreateOnsiteUpsell: boolean;
};

export const isCheckoutExperience = (card: ExperienceCreationCard) =>
  card.test?.key === "checkout_block_experiment" ||
  card.personalization?.kind === "checkoutBlock";

export const isPostPurchaseExperience = (card: ExperienceCreationCard) =>
  card.test?.key === "post_purchase_experiment" ||
  card.personalization?.kind === "postPurchase";

export const isOnsiteUpsellExperience = (card: ExperienceCreationCard) =>
  card.id === "bundle-onsite-upsell" ||
  card.test?.key === "onsite_upsell_experiment" ||
  (card.personalization?.kind === "mod" &&
    card.personalization.modToActivate === "onsiteUpsell");

// Gating that applies to the experience regardless of run mode. Shown on the
// first-page card. Checkout experiences require Shopify Plus, and
// post-purchase experiences require the same permissions the post-purchase
// table page gates on (PostPurchaseExperiences.route.tsx), for both tests and
// personalizations.
export const getFirstCardDisabledReason = (
  card: ExperienceCreationCard,
  {
    isPlusOrPartnerPlan,
    hasPostPurchaseAccess,
    hasWriteOrders,
    hasStorefrontToken,
    hasCartMetafields,
    canCreateOnsiteUpsell,
  }: FirstCardGatingContext
): ReactNode => {
  if (card.disabledReason) return card.disabledReason;

  if (isCheckoutExperience(card) && !isPlusOrPartnerPlan) {
    return (
      <IgTypography>
        Checkout experiences are only available on Shopify Plus plans. If you
        are on Shopify Plus and don't have access{" "}
        <IgLink
          onClick={() => {
            contactSupport();
          }}
        >
          contact support
        </IgLink>
        .
      </IgTypography>
    );
  }

  // Without feature access the card stays clickable so the user can reach the
  // "request access" flow; permission gates only apply once access exists.
  if (isPostPurchaseExperience(card) && hasPostPurchaseAccess) {
    if (!hasWriteOrders) {
      return "Post-purchase experiences require additional Shopify permissions. Please refresh your Shopify permissions to continue.";
    }
    if (!hasStorefrontToken) {
      return "Post-purchase experiences require a storefront access token. Please refresh your Shopify permissions to continue.";
    }
    if (!hasCartMetafields) {
      return "Post-purchase experiences require cart metafields to be enabled. Please contact support to enable this feature.";
    }
  }

  if (
    isOnsiteUpsellExperience(card) &&
    canCreateOnsiteUpsell &&
    !hasStorefrontToken
  ) {
    return "Onsite upsells require a storefront access token. Please refresh your Shopify permissions to continue.";
  }

  if (isCheckoutExperience(card) && !hasStorefrontToken) {
    return "Checkout experiences require a storefront access token. Please refresh your Shopify permissions to continue.";
  }

  return undefined;
};

export type TierGatingContext = {
  isCoreRedirectOnly: boolean;
  isOnCorePlan: boolean;
};

// Non-blocking tier chip carried over from the old CreateExperimentDialog:
// the test can be created but not launched until the plan is upgraded.
export const getOutOfScopeTier = (
  card: ExperienceCreationCard,
  { isCoreRedirectOnly, isOnCorePlan }: TierGatingContext
): "Core" | "Plus" | undefined => {
  const key = card.test?.key;
  if (!key || key === "split_test") return undefined;

  if (
    (CORE_CONTENT_TEST_KEYS as readonly string[]).includes(key) &&
    isCoreRedirectOnly
  ) {
    return "Core";
  }

  if ((PLUS_TEST_KEYS as readonly string[]).includes(key) && isOnCorePlan) {
    return "Plus";
  }

  return undefined;
};
